/* eslint-disable react-hooks/rules-of-hooks */
import {
  createRootRoute,
  Link,
  Outlet,
  useMatchRoute,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
} from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../trpc';
// Removed unused import: SubscriptionGuard
import { SubscriptionDialog } from '../components/SubscriptionDialog';
import { PaymentLockWall } from '../components/PaymentLockWall';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useIsAdmin } from '../utils/authHooks';
import { MessageNotification } from '../components/MessageNotification';
import { useUTMTracking } from '../utils/utmUtils';
import { Toaster } from '../components/ui/sonner';
import { ChatWidget } from '../components/ChatWidget';
import { BLOG_URL } from '../constants/urls';
import { Menu, X, GraduationCap } from 'lucide-react';
import { TourDialog } from '../components/TourDialog';
import { useUser } from '@clerk/clerk-react';

// Extend the Window interface to include Clerk
declare global {
  interface Window {
    Clerk?: {
      user: { id: string } | null;
      session: {
        getToken: () => Promise<string>;
      };
    };
  }
}

// Public routes configuration
export const publicRoutes = [
  '/',
  '/about',
  '/coming-soon',
  '/terms',
  '/privacy',
  '/faq',
  '/features',
  '/howItWorks',
  '/mobile',
  '/webPricing',
  '/delete-account',
  '/account-deleted',
];

// Check if site is in coming soon mode
const isComingSoonMode = () => import.meta.env.VITE_COMING_SOON_MODE === 'true';

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // If in coming soon mode and not already on the coming soon page, redirect
    if (isComingSoonMode() && location.pathname !== '/coming-soon') {
      throw redirect({
        to: '/coming-soon',
        search: {},
        hash: '',
      });
    }

    // Subscription check is now handled by the SubscriptionGuard component
    // So we don't need to do anything here
  },

  component: () => {
    const { t } = useTranslation();
    const router = useRouter();
    const matchRoute = useMatchRoute();
    const isHomePage = matchRoute({ to: '/' });
    const isAboutPage = matchRoute({ to: '/about' });
    const isComingSoonPage = matchRoute({ to: '/coming-soon' });
    const isTermsPage = matchRoute({ to: '/terms' });
    const isFaqPage = matchRoute({ to: '/faq' });
    const isFeaturesPage = matchRoute({ to: '/features' });
    const isHowItWorksPage = matchRoute({ to: '/howItWorks' });
    const isMobilePage = matchRoute({ to: '/mobile' });
    const isWebPricingPage = matchRoute({ to: '/webPricing' });
    const isDeleteAccountPage = matchRoute({ to: '/delete-account' });
    const isAccountDeletedPage = matchRoute({ to: '/account-deleted' });
    const isAdmin = useIsAdmin();
    const showBlogLink = import.meta.env.VITE_SHOW_BLOG_LINK === 'true';

    // Initialize UTM tracking (only active in production)
    useUTMTracking();

    // Check if current route is public
    const currentPath = router.state.location.pathname;
    const isPublicRoute = publicRoutes.includes(currentPath);


    const hideNavigation =
      isHomePage ||
      isAboutPage ||
      isComingSoonPage ||
      isTermsPage ||
      isFaqPage ||
      isFeaturesPage ||
      isHowItWorksPage ||
      isMobilePage ||
      isWebPricingPage ||
      isDeleteAccountPage ||
      isAccountDeletedPage ||
      isComingSoonMode();

    // State for subscription dialog
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    // State for payment update dialog
    const [paymentUpdateDialogOpen, setPaymentUpdateDialogOpen] =
      useState(false);
    // State for mobile menu
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    // State for tour dialog
    const [showTourDialog, setShowTourDialog] = useState(false);

    // Get current user from Clerk for tour tracking
    const { user: clerkUser } = useUser();
    const userId = clerkUser?.id;

    // Only fetch user data on protected routes
    const { data: user } = trpc.users.getCurrentUser.useQuery(undefined, {
      enabled: !isPublicRoute,
      retry: false,
      staleTime: Infinity,
    });

    // Get user's subscription plan with account status
    const { data: userPlan } = trpc.users.getUserPlan.useQuery(undefined, {
      enabled: !isPublicRoute,
      retry: false,
      staleTime: 60000, // Refresh every minute
    });

    // Get active subscription to check end date
    const { data: subscription } = trpc.stripe.getActiveSubscription.useQuery(
      undefined,
      {
        enabled:
          !isPublicRoute &&
          (userPlan?.accountStatus === 'canceling' ||
            userPlan?.accountStatus === 'canceled'),
        retry: false,
        staleTime: 60000, // Refresh every minute
      }
    );

    // Query for payment rejection status
    const { data: paymentStatus } = trpc.users.getPaymentStatus.useQuery(
      undefined,
      {
        enabled: !isPublicRoute && !!user,
        refetchInterval: 60000, // Check every minute
      }
    );

    // Subscription check handled by SubscriptionGuard component

    // Check for canceled account status and force logout
    useEffect(() => {
      if (!isPublicRoute && userPlan?.accountStatus === 'canceled') {
        console.log('User account is canceled - logging out from root');

        // Store error message to show after redirect
        window.sessionStorage.setItem(
          'access_error',
          'Your account has been canceled. Please reactivate your account to continue.'
        );

        // Redirect to home page (this will log them out effectively)
        window.location.href = '/';

        // Optional: If using Clerk explicitly log out
        if (typeof window !== 'undefined' && window.Clerk) {
          try {
            (window as any).Clerk.signOut();
          } catch (error) {
            console.error('Error signing out user:', error);
          }
        }
      }
    }, [userPlan, isPublicRoute]);

    // Calculate days remaining for canceling/canceled subscriptions
    const getDaysRemaining = () => {
      // First check if userPlan has accessUntil (added in our API update)
      if (userPlan?.accessUntil) {
        const endDate = new Date(userPlan.accessUntil);
        const today = new Date();

        // Set both dates to midnight to get accurate day count
        endDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Calculate difference in milliseconds and convert to days
        const differenceMs = endDate.getTime() - today.getTime();
        const daysDifference = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

        return daysDifference > 0 ? daysDifference : 0;
      }

      // Fallback to subscription data if available
      if (subscription?.currentPeriodEnd) {
        const endDate = new Date(subscription.currentPeriodEnd);
        const today = new Date();

        // Set both dates to midnight to get accurate day count
        endDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Calculate difference in milliseconds and convert to days
        const differenceMs = endDate.getTime() - today.getTime();
        const daysDifference = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

        return daysDifference > 0 ? daysDifference : 0;
      }

      // For trial users who are canceled/canceling, always show at least 1 day
      if (
        (userPlan?.accountStatus === 'canceled' ||
          userPlan?.accountStatus === 'canceling') &&
        userPlan?.planType === 'FREE_TRIAL'
      ) {
        return 1;
      }

      return null;
    };

    // Get days remaining for canceling/canceled accounts
    const daysRemaining =
      userPlan?.accountStatus === 'canceling' ||
      userPlan?.accountStatus === 'canceled'
        ? getDaysRemaining()
        : null;

    // Effect to handle payment rejection dialog
    useEffect(() => {
      if (!isPublicRoute && paymentStatus?.paymentRejected) {
        // const rejectionDate = paymentStatus.paymentRejectionDate
        //   ? new Date(paymentStatus.paymentRejectionDate)
        //   : null;

        // Check if dialog should be shown based on days since rejection or dialog dismissed status
        if (
          paymentStatus.daysSinceRejection >= 4 ||
          !paymentStatus.dialogDismissed
        ) {
          setPaymentUpdateDialogOpen(true);
        }
      }
    }, [paymentStatus, isPublicRoute]);

    // Development logging for subscription status
    useEffect(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Root - Subscription Status:', {
          userPlanStatus: userPlan?.accountStatus,
          subscription: subscription
            ? {
                id: subscription.id,
                status: subscription.status,
                currentPeriodEnd: subscription.currentPeriodEnd,
              }
            : 'No subscription data',
          daysRemaining,
          showCancellationBanner: daysRemaining !== null,
          paymentStatus: paymentStatus || 'No payment status data',
        });
      }
    }, [userPlan, subscription, daysRemaining, paymentStatus]);

    // Force redirect to coming soon page if mode changes while user is on the site
    useEffect(() => {
      if (isComingSoonMode() && !isComingSoonPage) {
        router.navigate({ to: '/coming-soon' });
      }
    }, [router, isComingSoonPage]);

    // Listen for subscription dialog open events and check URL parameters
    useEffect(() => {
      // Check URL parameters on load
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('openSubscription') === 'true') {
        setSubscriptionDialogOpen(true);
        // Clean up the URL parameter
        urlParams.delete('openSubscription');
        const newUrl = `${window.location.pathname}${
          urlParams.toString() ? '?' + urlParams.toString() : ''
        }`;
        window.history.replaceState({}, '', newUrl);
      }

      // Listen for custom events
      const handleOpenSubscriptionDialog = () => {
        setSubscriptionDialogOpen(true);
      };

      window.addEventListener(
        'openSubscriptionDialog',
        handleOpenSubscriptionDialog
      );

      return () => {
        window.removeEventListener(
          'openSubscriptionDialog',
          handleOpenSubscriptionDialog
        );
      };
    }, []);

    // Close mobile menu on window resize to desktop size
    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth >= 768) { // md breakpoint
          setMobileMenuOpen(false);
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
      <>
        {!hideNavigation && (
          <>
            <div className='p-4 flex items-center justify-between bg-white shadow-sm'>
              <div className='flex items-center'>
                <Link
                  to='/dashboard'
                  className='font-bold text-xl text-indigo-600 hover:text-indigo-800 transition-colors'
                >
                  Trackplicant
                </Link>
              </div>
              <div className='flex items-center gap-2 md:gap-4'>
                {/* User avatar - always visible */}
                <SignedIn>
                  <div className='md:hidden'>
                    <UserButton
                      afterSignOutUrl='/'
                      appearance={{
                        elements: {
                          userButtonAvatarBox: 'h-8 w-8',
                        },
                      }}
                    />
                  </div>
                </SignedIn>

                {/* Hamburger menu button - only visible on mobile */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className='md:hidden p-2 text-gray-700 hover:text-gray-900'
                  aria-label='Toggle menu'
                >
                  {mobileMenuOpen ? <X className='h-6 w-6' /> : <Menu className='h-6 w-6' />}
                </button>
                {/* Desktop menu - hidden on mobile */}
                <div className='hidden md:flex items-center gap-4'>
                <SignedIn>
                  {/* Countdown for canceling/canceled accounts */}
                  {daysRemaining !== null && (
                    <div className='bg-amber-50 border border-amber-200 rounded-md px-3 py-1 text-amber-700 flex items-center'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-4 w-4 mr-1'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      <span className='text-sm font-medium'>
                        {userPlan?.accountStatus === 'canceled'
                          ? 'Account canceled'
                          : userPlan?.accountStatus === 'canceling'
                            ? t('subscription.dialog.buttons.cancelingStatus')
                            : ''}
                        {' - '}
                        {daysRemaining === 1
                          ? '1 day'
                          : daysRemaining === 0
                            ? 'Expires today'
                            : `${daysRemaining} days`}{' '}
                        {daysRemaining > 0 &&
                          t('subscription.dialog.buttons.remaining')}
                      </span>
                      <Button
                        variant='link'
                        size='sm'
                        className='ml-2 text-green-600 p-0 h-auto text-xs'
                        onClick={() => setSubscriptionDialogOpen(true)}
                      >
                        Reactivate
                      </Button>
                    </div>
                  )}

                  {/* Blog link */}
                  {showBlogLink && (
                    <Button variant='outline' size='sm' asChild>
                      <a
                        href={BLOG_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label='Open Trackplicant blog (opens in a new tab)'
                      >
                        Blog
                      </a>
                    </Button>
                  )}

                  {/* Admin link - only show if user has admin role */}
                  {isAdmin && (
                    <Link
                      to='/admin'
                      className='text-sm font-medium mr-4 text-gray-900 hover:text-gray-700 transition-colors'
                    >
                      Admin
                    </Link>
                  )}

                  {/* Tour button - show for all non-admin users */}
                  {!isAdmin && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setShowTourDialog(true)}
                      className='flex items-center gap-2'
                    >
                      <GraduationCap className='h-4 w-4' />
                      Tour
                    </Button>
                  )}

                  {/* Hide Manage Subscription button for admin users */}
                  {!isAdmin && (
                    <Button
                      id='manage-subscription-button'
                      variant='outline'
                      size='sm'
                      onClick={() => setSubscriptionDialogOpen(true)}
                    >
                      Manage Account
                    </Button>
                  )}
                  <UserButton
                    afterSignOutUrl='/'
                    appearance={{
                      elements: {
                        userButtonAvatarBox: 'h-10 w-10',
                      },
                    }}
                  />
                  <div className='ml-4'>
                    <LanguageSwitcher />
                  </div>
                </SignedIn>
                <SignedOut>
                  {/* Blog link for signed-out users */}
                  {showBlogLink && (
                    <Button variant='outline' size='sm' asChild>
                      <a
                        href={BLOG_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label='Open Trackplicant blog (opens in a new tab)'
                      >
                        Blog
                      </a>
                    </Button>
                  )}
                  <SignInButton mode='modal' />
                  <div className='ml-4'>
                    <LanguageSwitcher />
                  </div>
                </SignedOut>
                </div>
                {/* End desktop menu */}
              </div>
            </div>

            {/* Mobile menu - slides down when open */}
            {mobileMenuOpen && (
              <div className='md:hidden bg-white border-t shadow-lg'>
                <div className='p-4 space-y-4'>
                  <SignedIn>
                    {/* Blog link */}
                    {showBlogLink && (
                      <a
                        href={BLOG_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='block py-2 text-gray-700 hover:text-indigo-600'
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Blog
                      </a>
                    )}

                    {/* Admin link for mobile */}
                    {isAdmin && (
                      <Link
                        to='/admin'
                        className='block py-2 text-gray-700 hover:text-indigo-600'
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    )}

                    {/* Tour button */}
                    {!isAdmin && (
                      <button
                        onClick={() => {
                          setShowTourDialog(true);
                          setMobileMenuOpen(false);
                        }}
                        className='block w-full text-left py-2 text-gray-700 hover:text-indigo-600 flex items-center gap-2'
                      >
                        <GraduationCap className='h-4 w-4' />
                        Tour
                      </button>
                    )}

                    {/* Manage Account button */}
                    {!isAdmin && (
                      <button
                        onClick={() => {
                          setSubscriptionDialogOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className='block w-full text-left py-2 text-gray-700 hover:text-indigo-600'
                      >
                        Manage Account
                      </button>
                    )}

                    {/* Language switcher */}
                    <div className='py-2'>
                      <LanguageSwitcher />
                    </div>
                  </SignedIn>

                  <SignedOut>
                    {/* Blog link for signed-out users */}
                    {showBlogLink && (
                      <a
                        href={BLOG_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='block py-2 text-gray-700 hover:text-indigo-600'
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Blog
                      </a>
                    )}

                    {/* Sign in button */}
                    <div className='py-2'>
                      <SignInButton mode='modal'>
                        <button
                          className='w-full text-left py-2 text-gray-700 hover:text-indigo-600'
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Sign In
                        </button>
                      </SignInButton>
                    </div>

                    {/* Language switcher */}
                    <div className='py-2'>
                      <LanguageSwitcher />
                    </div>
                  </SignedOut>
                </div>
              </div>
            )}
          </>
        )}

        {/* Always render the outlet - route protection happens in the beforeLoad hook */}
        <Outlet />

        {/* JCB */}
        {/* Add subscription dialog - only shown if user is defined */}
        {user && (
          <SubscriptionDialog
            open={subscriptionDialogOpen}
            onOpenChange={setSubscriptionDialogOpen}
            currentPlan={user.planType || 'FREE_TRIAL'}
            trialExpired={userPlan?.isTrialExpired || false}
          />
        )}

        {/* Payment update dialog — grace period only (dismissable nudge). Once the
            grace period ends (paymentUpdateRequired), the full-screen PaymentLockWall
            below supersedes this. */}
        {user &&
          paymentStatus?.paymentRejected &&
          !paymentStatus?.paymentUpdateRequired && (
            <SubscriptionDialog
              open={paymentUpdateDialogOpen}
              onOpenChange={(open) => {
                const canDismiss = paymentStatus.daysSinceRejection < 4;
                if (canDismiss || open) {
                  setPaymentUpdateDialogOpen(open);
                  if (!open && canDismiss) {
                    // Mark dialog as dismissed
                    trpc.users.dismissPaymentDialog.mutate();
                  }
                }
              }}
              currentPlan={user.planType || 'FREE_TRIAL'}
              paymentRejected={true}
              daysSinceRejection={paymentStatus.daysSinceRejection}
              canDismiss={paymentStatus.daysSinceRejection < 4}
            />
          )}

        {/* Hard lock — payment failed past the grace period. Full-screen wall that
            replaces the app; only Manage Subscription / Help & Support / Sign Out
            work. Admins and public routes are exempt. Mirrors the mobile lockout. */}
        {user &&
          !isPublicRoute &&
          !isAdmin &&
          paymentStatus?.paymentUpdateRequired && <PaymentLockWall />}

        {/* Tour dialog */}
        <TourDialog
          open={showTourDialog}
          onOpenChange={setShowTourDialog}
          onComplete={() => {
            // Mark tour as completed when relaunched
            if (userId) {
              localStorage.setItem(`tour_completed_${userId}`, 'true');
            }
            setShowTourDialog(false);
          }}
        />

        {process.env.NODE_ENV !== 'production' &&
          import.meta.env.VITE_SHOW_DEBUG_BUTTONS === 'true' && (
            <TanStackRouterDevtools />
          )}

        {/* Show message notifications for signed-in users */}
        <SignedIn>
          <MessageNotification />
        </SignedIn>

        {/* Chat widget - only show for signed-in users on non-public routes */}
        <SignedIn>{!isPublicRoute && <ChatWidget />}</SignedIn>

        <Toaster />
      </>
    );
  },
});
